/**
 * The catalog. Order here is the display order on the pricing page.
 * Annual variants share their limits + features with the monthly twin
 * and only differ in price/cadence — built programmatically below.
 */
const MONTHLY_PRICES = {
    indie: { usd: 5 },
    pro: { usd: 10 },
    team: { usd: 20 },
};
/** Annual discount ratio (~17% = "2 months free"). */
const ANNUAL_DISCOUNT_RATIO = 10 / 12;
function annualOf(monthlyUsd) {
    const annual = Math.round(monthlyUsd * 12 * ANNUAL_DISCOUNT_RATIO);
    return `$${annual} / year`;
}
export const PLAN_CATALOG = {
    free: {
        id: 'free',
        name: 'Free',
        tagline: 'For soloists and small teams getting started.',
        displayPrice: '$0',
        cadence: 'monthly',
        currency: 'USD',
        // Free tier: a single project with up to 5 members. Enforced on
        // project create (ProjectsHandler) — a free org can hold exactly
        // one project.
        limits: { maxProjects: 1, maxMembers: 5 },
        features: {
            customBranding: false,
            emailDigests: false,
            sso: false,
            auditLog: false,
            hidePoweredBy: false,
            support: 'community',
            sla: null,
        },
        ctaLabel: 'Start free',
    },
    indie: {
        id: 'indie',
        name: 'Indie',
        tagline: 'For solo builders shipping more.',
        displayPrice: `$${MONTHLY_PRICES.indie.usd} / month`,
        cadence: 'monthly',
        currency: 'USD',
        limits: { maxProjects: 5, maxMembers: 10 },
        features: {
            customBranding: true,
            emailDigests: true,
            sso: false,
            auditLog: false,
            hidePoweredBy: true,
            support: 'email',
            sla: null,
        },
        ctaLabel: 'Start 14-day trial',
    },
    indie_annual: {
        id: 'indie_annual',
        name: 'Indie (Annual)',
        tagline: 'Save ~17% — paid yearly.',
        displayPrice: annualOf(MONTHLY_PRICES.indie.usd),
        cadence: 'annual',
        currency: 'USD',
        limits: { maxProjects: 5, maxMembers: 10 },
        features: {
            customBranding: true,
            emailDigests: true,
            sso: false,
            auditLog: false,
            hidePoweredBy: true,
            support: 'email',
            sla: null,
        },
        ctaLabel: 'Start 14-day trial',
    },
    pro: {
        id: 'pro',
        name: 'Pro',
        tagline: 'For growing teams and projects.',
        displayPrice: `$${MONTHLY_PRICES.pro.usd} / month`,
        cadence: 'monthly',
        currency: 'USD',
        limits: { maxProjects: 10, maxMembers: 15 },
        features: {
            customBranding: true,
            emailDigests: true,
            sso: false,
            auditLog: false,
            hidePoweredBy: true,
            support: 'email',
            sla: null,
        },
        ctaLabel: 'Start 14-day trial',
        popular: true,
    },
    pro_annual: {
        id: 'pro_annual',
        name: 'Pro (Annual)',
        tagline: 'Save ~17% — paid yearly.',
        displayPrice: annualOf(MONTHLY_PRICES.pro.usd),
        cadence: 'annual',
        currency: 'USD',
        limits: { maxProjects: 10, maxMembers: 15 },
        features: {
            customBranding: true,
            emailDigests: true,
            sso: false,
            auditLog: false,
            hidePoweredBy: true,
            support: 'email',
            sla: null,
        },
        ctaLabel: 'Start 14-day trial',
    },
    team: {
        id: 'team',
        name: 'Team',
        tagline: 'For companies that need room to grow.',
        displayPrice: `$${MONTHLY_PRICES.team.usd} / month`,
        cadence: 'monthly',
        currency: 'USD',
        limits: { maxProjects: null, maxMembers: null },
        features: {
            customBranding: true,
            emailDigests: true,
            sso: true,
            auditLog: true,
            hidePoweredBy: true,
            support: 'slack',
            sla: null,
        },
        ctaLabel: 'Start 14-day trial',
    },
    team_annual: {
        id: 'team_annual',
        name: 'Team (Annual)',
        tagline: 'Save ~17% — paid yearly.',
        displayPrice: annualOf(MONTHLY_PRICES.team.usd),
        cadence: 'annual',
        currency: 'USD',
        limits: { maxProjects: null, maxMembers: null },
        features: {
            customBranding: true,
            emailDigests: true,
            sso: true,
            auditLog: true,
            hidePoweredBy: true,
            support: 'slack',
            sla: null,
        },
        ctaLabel: 'Start 14-day trial',
    },
    enterprise: {
        id: 'enterprise',
        name: 'Enterprise',
        tagline: 'Dedicated capacity, SLA, and priority support.',
        displayPrice: null,
        cadence: 'contact',
        currency: null,
        limits: { maxProjects: null, maxMembers: null },
        features: {
            customBranding: true,
            emailDigests: true,
            sso: true,
            auditLog: true,
            hidePoweredBy: true,
            support: 'csm',
            sla: 99.9,
        },
        ctaLabel: 'Contact us',
    },
    // Internal, unbilled, unlimited tier — used for the platform operator's own
    // org (superadmin) and the synthetic default org. Excluded from
    // PUBLIC_PLAN_ORDER, so it never appears on the pricing page.
    unlimited: {
        id: 'unlimited',
        name: 'Internal',
        tagline: 'Platform / internal — unlimited, unbilled.',
        displayPrice: null,
        cadence: 'contact',
        currency: null,
        limits: { maxProjects: null, maxMembers: null },
        features: {
            customBranding: true,
            emailDigests: true,
            sso: true,
            auditLog: true,
            hidePoweredBy: true,
            support: 'community',
            sla: null,
        },
        ctaLabel: 'Contact us',
    },
};
/** Plans shown on the marketing pricing card in display order.
 *  The marketing page lays `free` out on its own row above the paid
 *  tiers and `enterprise` on its own row below them. */
export const PUBLIC_PLAN_ORDER = ['free', 'indie', 'pro', 'team', 'enterprise'];
/** Get the catalog entry; throws on unknown id (safe — every PlanId is exhaustive). */
export function getPlan(id) {
    const plan = PLAN_CATALOG[id];
    if (!plan)
        throw new Error(`Unknown plan id: ${id}`);
    return plan;
}
/** True if `current` has the listed feature; reads catalog. */
export function planHas(current, feature) {
    return Boolean(getPlan(current).features[feature]);
}
/** Annual companion of a given monthly plan, or null when none. */
export function annualVariant(id) {
    if (id === 'indie')
        return 'indie_annual';
    if (id === 'pro')
        return 'pro_annual';
    if (id === 'team')
        return 'team_annual';
    return null;
}
/** Monthly companion of a given annual plan, or null when none. */
export function monthlyVariant(id) {
    if (id === 'indie_annual')
        return 'indie';
    if (id === 'pro_annual')
        return 'pro';
    if (id === 'team_annual')
        return 'team';
    return null;
}
//# sourceMappingURL=plans.js.map